import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { Minus, Plus, X } from "lucide-react";
import { resolveImageUrl } from "../lib/api";

export default function CartDrawer() {
  const { items, open, setOpen, remove, updateQty, subtotal, clear, priceOf } = useCart();
  const { user, requireAuth } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!items.length) return;
    setOpen(false);
    if (!user) {
      requireAuth(() => navigate("/checkout"));
      return;
    }
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="bg-[var(--hk-bg)] border-l border-white/10 text-white w-full sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-white/10 pb-4">
          <SheetTitle className="font-display text-2xl uppercase tracking-tight text-white">
            Your Bag
          </SheetTitle>
          <SheetDescription className="sr-only">Your shopping bag items and checkout</SheetDescription>
        </SheetHeader>

        <div className="flex flex-col h-[calc(100%-4rem)]">
          <div className="flex-1 overflow-y-auto py-6 space-y-5">
            {items.length === 0 ? (
              <div className="text-center text-gray-500 mt-20">
                <p className="font-display uppercase text-xl text-white mb-2">Empty bag</p>
                <p className="text-sm">Nothing copped yet.</p>
              </div>
            ) : (
              items.map((i) => {
                const unit = priceOf(i);
                const minAllowed = i.min_qty || 1;
                const modeLabel = i.batch_mode === "wholesale" ? "Wholesale" : "Small batch";
                const sub = i.package_label ? `${modeLabel} · ${i.package_label}` : modeLabel;
                return (
                  <div
                    key={`${i.product.id}-${i.batch_mode}-${i.package_key || "_"}`}
                    data-testid={`cart-item-${i.product.id}-${i.batch_mode}-${i.package_key || "default"}`}
                    className="flex gap-4 border-b border-white/5 pb-5"
                  >
                    <img src={resolveImageUrl(i.product.image)} alt={i.product.name}
                         className="w-20 h-20 object-cover border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <p className="font-display uppercase text-sm text-white truncate">
                        {i.product.name}
                      </p>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">
                        {sub}
                      </p>
                      <p className="text-[var(--hk-orange)] font-bold mt-1">
                        ${(unit * i.quantity).toFixed(2)}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <button
                          onClick={() => updateQty(i.product.id, i.batch_mode, i.package_key, Math.max(minAllowed, i.quantity - 1))}
                          className="p-1 border border-white/20 hover:border-white/50"
                          data-testid={`qty-down-${i.product.id}-${i.batch_mode}-${i.package_key || "default"}`}
                        >
                          <Minus size={12} />
                        </button>
                        <span className="text-sm w-5 text-center">{i.quantity}</span>
                        <button
                          onClick={() => updateQty(i.product.id, i.batch_mode, i.package_key, i.quantity + 1)}
                          className="p-1 border border-white/20 hover:border-white/50"
                          data-testid={`qty-up-${i.product.id}-${i.batch_mode}-${i.package_key || "default"}`}
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                    <button
                      data-testid={`remove-${i.product.id}-${i.batch_mode}-${i.package_key || "default"}`}
                      onClick={() => remove(i.product.id, i.batch_mode, i.package_key)}
                      className="text-gray-500 hover:text-red-400 self-start"
                    >
                      <X size={18} />
                    </button>
                  </div>
                );
              })
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-white/10 pt-4 space-y-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 uppercase tracking-wider">Subtotal</span>
                <span className="font-display text-xl text-white">${subtotal.toFixed(2)}</span>
              </div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500">
                Cash on delivery · Free local delivery
              </p>
              <button
                onClick={handleCheckout}
                data-testid="checkout-cod-btn"
                className="w-full bg-[var(--hk-orange)] text-black font-display font-bold uppercase tracking-wider py-4 rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors"
              >
                {user ? "Checkout — cash on delivery" : "Sign in to checkout"}
              </button>
              <button
                onClick={clear}
                className="w-full text-xs uppercase tracking-widest text-gray-500 hover:text-white"
              >
                Clear bag
              </button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
